import { z } from 'zod'
import { LeafPrimitiveSchema, type LeafPrimitive, PrintRotationSchema } from './primitives'
import { parseLegacyBlockVersion } from './version'

export const BakedBlockPlacementSchema = z.object({
  widthMm: z.number().positive(),
  heightMm: z.number().positive(),
  hAlign: z.enum(['left', 'center', 'right']),
  vAlign: z.enum(['top', 'center', 'bottom']),
})
export type BakedBlockPlacement = z.infer<typeof BakedBlockPlacementSchema>

export type BakedBlockInstance = {
  type: 'baked-block-instance'
  id: string
  blockId: string
  blockVersion: number
  bakedAt: string
  bakedFrom?: string
  props: Record<string, unknown>
  expansion: Zone
  placement: BakedBlockPlacement
}

export type ZoneComponent = LeafPrimitive | BakedBlockInstance

export const BorderEdgeSchema = z.object({
  thicknessMm: z.number().positive(),
  style: z.enum(['solid', 'dashed', 'dotted']),
})
export type BorderEdge = z.infer<typeof BorderEdgeSchema>

export const RegionBordersSchema = z.object({
  top: BorderEdgeSchema.optional(),
  right: BorderEdgeSchema.optional(),
  bottom: BorderEdgeSchema.optional(),
  left: BorderEdgeSchema.optional(),
})
export type RegionBorders = z.infer<typeof RegionBordersSchema>

export const ZoneStyleSchema = z.object({
  backgroundColor: z.string().optional(),
})
export type ZoneStyle = z.infer<typeof ZoneStyleSchema>

export type Zone = {
  id: string
  split?: { direction: 'row' | 'column'; ratio: number }
  children?: [Zone, Zone]
  component?: ZoneComponent
  absolutePosition?: { x: number; y: number; w: number; h: number }
  style?: ZoneStyle
  borders?: RegionBorders
}

export const SplitSchema = z.object({
  direction: z.enum(['row', 'column']),
  ratio: z.number().min(0.05).max(0.95),
})
export type Split = z.infer<typeof SplitSchema>

export const AbsolutePositionSchema = z.object({
  x: z.number(),
  y: z.number(),
  w: z.number().nonnegative(),
  h: z.number().nonnegative(),
})

export const ZoneComponentSchema: z.ZodType<ZoneComponent> = z.lazy(() =>
  z.union([LeafPrimitiveSchema, BakedBlockInstanceSchema])
)

export const ZoneSchema: z.ZodType<Zone> = z.lazy(() =>
  z
    .object({
      id: z.string(),
      split: SplitSchema.optional(),
      children: z.tuple([ZoneSchema, ZoneSchema]).optional(),
      component: ZoneComponentSchema.optional(),
      absolutePosition: AbsolutePositionSchema.optional(),
      style: ZoneStyleSchema.optional(),
      borders: RegionBordersSchema.optional(),
    })
    .refine(
      z =>
        (z.split !== undefined && z.children !== undefined && z.component === undefined) ||
        (z.split === undefined && z.children === undefined && z.component !== undefined),
      {
        message:
          'zone must have either { split, children } or a single { component }, but not both and not neither',
      }
    )
)

export const BakedBlockInstanceSchema: z.ZodType<BakedBlockInstance> = z.lazy(() =>
  z.object({
    type: z.literal('baked-block-instance'),
    id: z.string(),
    blockId: z.string(),
    blockVersion: z.number().int().positive(),
    bakedAt: z.string().datetime(),
    bakedFrom: z.string().optional(),
    props: z.record(z.string(), z.unknown()),
    expansion: ZoneSchema,
    placement: BakedBlockPlacementSchema,
  })
)

const TemplateBaseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  widthMm: z.number().positive(),
  heightMm: z.number().positive(),
  /**
   * Designer-only UI state: when true, the editors disable label
   * width/height editing (typed ruler inputs + resize drag handles).
   * The ZPL pipeline ignores this field — no print-output impact.
   */
  dimensionsLocked: z.boolean().optional(),
  /**
   * Print-time 90° rotation to fit a label wider than the print head.
   * UNLIKE `dimensionsLocked`, this AFFECTS ZPL OUTPUT — part of the canonical
   * persisted body. Absent ⇒ treated as { mode:'auto', direction:'cw' } by the
   * pipeline. Do NOT add it to deepEqualBlockBody (that helper is block-only;
   * a change here legitimately changes the print).
   */
  printRotation: PrintRotationSchema.optional(),
  rootZone: ZoneSchema,
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
  createdBy: z.string().optional(),
})

export const TemplateSchema = TemplateBaseSchema
export type Template = z.infer<typeof TemplateSchema>

export const TemplateMetaSchema = TemplateBaseSchema.pick({
  id: true,
  name: true,
  description: true,
  widthMm: true,
  heightMm: true,
  updatedAt: true,
})
export type TemplateMeta = z.infer<typeof TemplateMetaSchema>

const DEFAULT_BAKED_PLACEMENT_ALIGNMENT = { hAlign: 'center', vAlign: 'center' } as const

/**
 * Migration helper: parses a `BakedBlockInstance` from an arbitrary input,
 * filling `placement` with a host-filling centred default when absent.
 * Use this when reading legacy templates whose baked instances predate
 * the `placement` field — they continue to render exactly as before
 * because the default placement fills the entire host zone.
 *
 * `host` must be the host-zone box (the leaf's computed box at the
 * baked instance's location). Callers that already know the box from
 * a parent layout pass should reuse it; otherwise compute it from the
 * template's `computeZoneLayout`.
 */
export function withBakedPlacementDefault(
  input: unknown,
  host: { widthMm: number; heightMm: number }
): BakedBlockInstance {
  const candidate =
    typeof input === 'object' && input !== null
      ? (input as Record<string, unknown>)
      : {}
  const patched = {
    ...candidate,
    blockVersion: parseLegacyBlockVersion(candidate.blockVersion),
    placement:
      candidate.placement ??
      {
        widthMm: host.widthMm,
        heightMm: host.heightMm,
        ...DEFAULT_BAKED_PLACEMENT_ALIGNMENT,
      },
  }
  return BakedBlockInstanceSchema.parse(patched)
}
